import React from 'react';
import YouTube from 'react-youtube';
import { useMusic } from './context/MusicContext';
import { useScoreboard } from './context/ScoreboardContext';
import { useMedia } from './context/MediaContext';

const MusicOverlay = ({ onClose }) => {
  const playerRef = React.useRef(null);
  const { isMusicPlaying, setIsMusicPlaying, musicVolume, setMusicVolume } = useMusic();
  const { isEntertainmentEnabled } = useScoreboard();
  const { youtubePlaylistUrl } = useMedia();

  if (!isEntertainmentEnabled) return null;

  return (
    <div className="fixed bottom-16 right-4 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-3 w-[320px] flex flex-col items-center shadow-lg">
        <div className="w-full flex justify-between items-center mb-2">
          <h2 className="font-bold text-black text-lg">Youtube Music Player</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="w-full mb-3 rounded-lg overflow-hidden">

          {youtubePlaylistUrl ? (
            <YouTube
              ref={playerRef}
              videoId={youtubePlaylistUrl.includes('list=') ? undefined : youtubePlaylistUrl.split('v=')[1]}
              opts={{
                width: '100%',
                height: '180',
                playerVars: {
                  // https://developers.google.com/youtube/player_parameters
                  autoplay: 0, // Always start paused
                  controls: 1,
                  modestbranding: 1,
                  playsinline: 1,
                  rel: 0,
                  showinfo: 1,
                  // Playlist settings
                  listType: youtubePlaylistUrl.includes('list=') ? 'playlist' : undefined,
                  list: youtubePlaylistUrl.includes('list=') ? new URLSearchParams(youtubePlaylistUrl.split('?')[1]).get('list') : undefined
                }
              }}
              onError={(e) => {
                setIsMusicPlaying(false);
              }}
              onReady={(event) => {
                playerRef.current = event.target;
                event.target.setVolume(musicVolume * 100);
              }}
              onPlay={() => {
                setIsMusicPlaying(true);
              }}
              onPause={() => {
                setIsMusicPlaying(false);
              }}
              onEnd={() => {
                if (!youtubePlaylistUrl.includes('list=')) {
                  setIsMusicPlaying(false);
                }
              }}
              onStateChange={(event) => {
                // Update volume when changed externally (through YouTube controls)
                const newVolume = event.target.getVolume() / 100;
                if (Math.abs(newVolume - musicVolume) > 0.01) {
                  setMusicVolume(newVolume);
                }
              }}
            />
          ) : (
            <div className="h-[180px] bg-gray-100 flex items-center justify-center text-gray-500">
              No playlist URL set
            </div>
          )}
        </div>
        <div className="flex justify-center gap-2 mt-2">
          <button
            onClick={() => {
              try {
                if (playerRef.current?.getPlayerState) {
                  if (isMusicPlaying) {
                    playerRef.current.pauseVideo();
                  } else {
                    playerRef.current.playVideo();
                  }
                  setIsMusicPlaying(!isMusicPlaying);
                } else {
                  console.warn('YouTube player is not ready yet');
                }
              } catch (error) {
                console.error('Error controlling YouTube player:', error);
                setIsMusicPlaying(false);
              }
            }}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg px-4 py-2 flex items-center justify-center transition-colors duration-200"
            title={isMusicPlaying ? 'Pause' : 'Play'}
          >
            {isMusicPlaying ? 'Pause' : 'Play'}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-black font-medium">Volume:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={musicVolume}
              onChange={(e) => {
                const newVolume = parseFloat(e.target.value);
                setMusicVolume(newVolume);
                if (playerRef.current) {
                  playerRef.current.setVolume(newVolume * 100);
                }
              }}
              className="w-24 accent-amber-500"
              title="Volume"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicOverlay;
